import type { JSX } from 'react';

import { Checkbox } from '@/components/ui/checkbox';
import { Label } from '@/components/ui/label';
import { cn } from '@/lib/utils/cn';
import type { PermissionsResponse } from '@/types/permissions';

/**
 * Plan 07-02 permission picker.
 *
 * Stateless / fully controlled. Groups permissions by module and renders a
 * "Select all in {module}" toggle plus an individual checkbox per action.
 *
 * Props:
 *  - `value` — readonly array of selected permission codes (controlled).
 *  - `onChange` — invoked with the next selection.
 *  - `permissions` — registry shape returned by GET /api/permissions.
 *  - `readOnly` — when true, all checkboxes are disabled (used by RoleDetailPage).
 *  - `disabled` — alias for readOnly used by RoleForm submit-in-flight state.
 */
export interface PermissionPickerProps {
  readonly value: readonly string[];
  readonly onChange?: (next: string[]) => void;
  readonly permissions: PermissionsResponse;
  readonly readOnly?: boolean;
  readonly disabled?: boolean;
  readonly className?: string;
}

export function PermissionPicker({
  value,
  onChange,
  permissions,
  readOnly = false,
  disabled = false,
  className,
}: PermissionPickerProps): JSX.Element {
  const selected = new Set(value);
  const interactive = !readOnly && !disabled;

  function commit(next: ReadonlySet<string>): void {
    if (!interactive) return;
    onChange?.([...next].sort());
  }

  function toggleAction(code: string): void {
    const next = new Set(selected);
    if (next.has(code)) next.delete(code);
    else next.add(code);
    commit(next);
  }

  function toggleModule(
    moduleName: string,
    allCodes: readonly string[],
    allChecked: boolean,
  ): void {
    const next = new Set(selected);
    if (allChecked) {
      // remove all module codes
      for (const c of allCodes) next.delete(c);
    } else {
      // add all module codes
      for (const c of allCodes) next.add(c);
    }
    commit(next);
    // moduleName is a label-only param for callers; suppress unused warning.
    void moduleName;
  }

  return (
    <div className={cn('space-y-4', className)} data-slot="permission-picker">
      {permissions.length === 0 && (
        <p className="text-sm text-muted-foreground" data-testid="permission-picker-empty">
          No permissions available.
        </p>
      )}
      {permissions.map((mod) => {
        const moduleCodes = mod.actions.map((a) => a.code);
        const checkedCount = moduleCodes.filter((c) => selected.has(c)).length;
        const allChecked = checkedCount === moduleCodes.length && moduleCodes.length > 0;
        const someChecked = checkedCount > 0 && !allChecked;
        return (
          <fieldset
            key={mod.module}
            className="rounded-md border bg-card p-4"
            data-testid={`permission-picker-module-${mod.module}`}
          >
            <legend className="px-1 text-sm font-semibold capitalize">{mod.module}</legend>
            <div className="mb-3 flex items-center gap-2">
              <Checkbox
                id={`pp-select-all-${mod.module}`}
                data-testid={`permission-picker-select-all-${mod.module}`}
                checked={allChecked ? true : someChecked ? 'indeterminate' : false}
                disabled={!interactive}
                onCheckedChange={() => {
                  toggleModule(mod.module, moduleCodes, allChecked);
                }}
              />
              <Label htmlFor={`pp-select-all-${mod.module}`} className="text-xs font-medium">
                Select all in {mod.module}
              </Label>
            </div>
            <ul className="space-y-2">
              {mod.actions.map((action) => {
                const checked = selected.has(action.code);
                return (
                  <li key={action.code} className="flex items-start gap-2">
                    <Checkbox
                      id={`pp-${action.code}`}
                      data-testid={`permission-picker-action-${action.code}`}
                      checked={checked}
                      disabled={!interactive}
                      onCheckedChange={() => {
                        toggleAction(action.code);
                      }}
                      className="mt-0.5"
                    />
                    <Label htmlFor={`pp-${action.code}`} className="flex-1 cursor-pointer">
                      <div className="font-mono text-xs">{action.code}</div>
                      <div className="text-xs text-muted-foreground">{action.description}</div>
                    </Label>
                  </li>
                );
              })}
            </ul>
          </fieldset>
        );
      })}
    </div>
  );
}
